import React from "react";
import { useApp } from "../../context/AppContext";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import ProgressBar from "../../components/ui/ProgressBar";
import PageShell from "../../components/layout/PageShell";

interface Props { onNavigate: (tab: string) => void; }

const SuperAdminDashboard: React.FC<Props> = ({ onNavigate }) => {
  const { orgs, users, exams, results, traits, assessment } = useApp();

  return (
    <PageShell title="Super Admin Dashboard" subtitle="System-wide overview and controls">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Organizations" value={orgs.length}  icon="🏢" color="#6366f1" sub={`${orgs.filter(o=>o.status==="active").length} active`}   onClick={() => onNavigate("organizations")} />
        <StatCard label="Total Users"   value={users.length} icon="👥" color="#10b981" sub={`${users.filter(u=>u.status==="active").length} active`}   onClick={() => onNavigate("users")} />
        <StatCard label="Assessment" value={assessment.length} icon="📋" color="#f59e0b" sub={`${assessment.filter(t=>t.isActive===true).length} active`}   onClick={() => onNavigate("manage_assessment_templates")} />
        <StatCard label="Results"       value={results.length} icon="📊" color="#ec4899" sub="Total submissions" onClick={() => onNavigate("results")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Orgs list */}
        <GlassCard className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800">Organizations</h3>
            <Button size="sm" variant="secondary" onClick={() => onNavigate("organizations")}>View All →</Button>
          </div>
          <div className="space-y-3">
            {orgs.map(org => (
              <div key={org.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/40 hover:bg-white/60 transition-colors">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-extrabold text-sm flex-shrink-0 shadow"
                  style={{ background: org.primaryColor }}>
                  {org.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-800 truncate">{org.name}</p>
                  <p className="text-xs text-gray-400">{org.candidateCount} candidates · {org.plan}</p>
                </div>
                <Badge text={org.status} />
                <Badge text={org.plan} />
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Trait overview */}
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800">Traits</h3>
            <Button size="sm" variant="secondary" onClick={() => onNavigate("traits")}>Manage</Button>
          </div>
          <div className="space-y-4">
            {traits.map(t => (
              <div key={t.id}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-base">{t.name}</span>
                    <span className="text-sm font-semibold text-gray-700">{t.name}</span>
                  </div>
                  <span className="text-xs text-gray-400">10 Q</span>
                </div>
                <ProgressBar value={70} max={100} height={6} />
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Exams */}
      <GlassCard className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800">Recent Exams</h3>
          <Button size="sm" variant="secondary" onClick={() => onNavigate("exams")}>View All →</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {exams.map(exam => (
            <div key={exam.id} className="p-4 rounded-xl bg-white/40 hover:bg-white/70 transition-colors border border-white/30">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge text={exam.status} />
                <Badge text={exam.type} />
              </div>
              <h4 className="font-bold text-gray-800 text-sm leading-snug mt-1">{exam.title}</h4>
              <p className="text-xs text-gray-400 mt-2">⏱ {exam.duration}min · ❓ {exam.totalQuestions}Qs</p>
              <div className="mt-2">
                <p className="text-xs text-gray-400 mb-1">Assigned: {exam.assignedCount}</p>
                <ProgressBar value={exam.assignedCount} max={200} color="#6366f1" height={4} />
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
    </PageShell>
  );
};

export default SuperAdminDashboard;
